import { useMemo } from 'react';
import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';
import { moveDestinations } from '../../../lib/dinnerClaimEdit';
import { formatInZone } from '../../../lib/tripTime';
import type { GroupDinnerHold } from '../../../types/groups';

/**
 * Which table to move this guest to.
 *
 * Same-evening tables come first, because a party changing tables tonight is the
 * operation a host actually performs standing up. That ordering is presentation
 * and nothing else — Studio does not pre-filter the destinations the server
 * might refuse. The one-table-per-guest-per-night rule lives on the server, a
 * copy of it here would be a second rule that can disagree, and a same-evening
 * A-to-B move is the legitimate case rather than the forbidden one. The same
 * reasoning `DinnerGuestPicker` gives for not filtering its roster.
 *
 * Full tables are listed and disabled rather than hidden. A table that vanishes
 * explains nothing, and a host who knows the restaurant is expecting them will
 * otherwise conclude Studio has lost the booking.
 */
export default function MoveClaimPicker({
  holds,
  fromHold,
  partySize,
  timeZone,
  disabled,
  onPick,
}: {
  /** Every non-cancelled hold on this trip — the filtering that matters is done here. */
  holds: GroupDinnerHold[];
  fromHold: GroupDinnerHold;
  /**
   * The size of the party being moved. A table with one seat free cannot take a
   * party of two, and offering it enabled is how the host taps a table the
   * server will refuse and is told it "filled up while you were looking" about a
   * table whose availability never changed.
   */
  partySize: number;
  timeZone: string;
  disabled: boolean;
  onPick: (hold: GroupDinnerHold) => void;
}) {
  const destinations = useMemo(
    () => moveDestinations(holds, fromHold, timeZone),
    [holds, fromHold, timeZone],
  );

  if (destinations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        There is no other table to move them to — this trip has only this one.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {destinations.map(({ hold, sameEvening }) => {
        // The server's rule, not a looser one: availableAtDestination < partySize.
        const seats = Math.max(1, Math.floor(Number(partySize) || 1));
        const full = hold.availableSpots < seats;
        return (
          <ButtonBase
            key={hold.id}
            disabled={disabled || full}
            onClick={() => onPick(hold)}
            aria-label={`Move to ${hold.venueName}`}
            sx={{
              width: '100%',
              minHeight: 56,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              justifyContent: 'space-between',
              textAlign: 'left',
              opacity: full ? 0.5 : 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{hold.venueName}</Typography>
                {/* Says out loud why this table is at the top of the list. */}
                {sameEvening ? <Chip size="small" color="primary" variant="outlined" label="same evening" /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatInZone(hold.reservedAt, hold.timeZone || timeZone)}
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, pl: 1 }}>
              {/*
                "full" and "too small for this party" are different refusals and
                a host acts on them differently — one waits for a release, the
                other splits the party. Saying "full" about a table with a seat
                in it, while the row is greyed out, reads as a bug.
              */}
              <Chip
                size="small"
                color={full ? 'default' : 'success'}
                label={
                  hold.availableSpots <= 0
                    ? 'full'
                    : full
                      ? `only ${hold.availableSpots} left`
                      : `${hold.availableSpots} left`
                }
              />
            </Box>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
